/* eslint-disable no-nested-ternary */
import { InputNumber, Radio, Switch, Input } from 'antd';
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import PreRequisitesSwitch from './PreRequisitesSwitch';
import useQuotesContext from './QuotesContext';
import { radioStyle } from '../../utils/constants/misc';
import useAuthContext from '../../contexts/AuthContext';

import { maxModifiedValue } from '../contracts/maxModifiedValue';

const DEFAULT_COMMISSION_FEE = 40;
const maxComissionFeeMap = {
  mriwk: 30,
  rcpml: 30,
  mrpml: 30,
  mrimlml: 30,
  pnomlml: 30,
  rcngawk: 20,
  association: 30,
  rcphx: 30,
  rcgls: 20
};

const useSharedFields = ({ terminationNonPayment }, hiddenFieldNames = []) => {
  const { t } = useTranslation();
  const { formValues, displayRestricted, setFormValues } = useQuotesContext();
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const type = pathSegments[2];
  const queryParams = new URLSearchParams(location.search);
  const supplier =
    type === 'mriml' || type === 'pnoml' ? 'ml' : queryParams.get('sup');
  const { getProductAccess, user } = useAuthContext();
  const params_asso = new URLSearchParams(location.search);
  const assoType = params_asso.get('assoT');
  const [params] = useState(getProductAccess(type)?.advancedManagement);
  const isAmendment = window.location.href.search('amendment');

  const hiddenFieldNameMap = useMemo(() => {
    const map = new Map();

    if (!Array.isArray(hiddenFieldNames)) {
      return map;
    }

    hiddenFieldNames.forEach((hiddenFieldName) => map.set(hiddenFieldName));

    return map;
  }, hiddenFieldNames);

  const getMaxPaperworkFee = () => {
    if (type !== 'prevoyance') {
      if (formValues?.paperwork_fee_payment === 'ASSURMAX') {
        if (
          params?.controlled_fit?.selected &&
          params?.controlled_fit?.administration_fees_maxVal < 101
        )
          return params?.controlled_fit?.administration_fees_maxVal;
        return 101;
      }
      if (params?.controlled_fit?.selected) {
        return params?.controlled_fit?.administration_fees_maxVal;
      }
      if (typeof maxModifiedValue.paperwork_fee[type] === 'number') {
        return maxModifiedValue.paperwork_fee[type];
      }
      return 1999;
    }
    if (formValues?.paperwork_fee_payment === 'ASSURMAX') {
      if (
        params?.controlled_fit?.selected &&
        params?.controlled_fit?.administration_fees_maxVal < 10
      )
        return params?.controlled_fit?.administration_fees_maxVal;
      return 10;
    }
    return 50;
  };

  const getMaxBrokerFee = () => {
    if (params?.controlled_fit?.selected) {
      return params?.controlled_fit?.commission_rate_maxVal;
    }
    if (type === 'prevoyance') {
      return 10;
    }
    if (typeof maxModifiedValue.broker_fee[type] === 'number') {
      return maxModifiedValue.broker_fee[type];
    }
    return 5;
  };

  const getBrokerFeeLabel = () => {
    if (
      formValues &&
      formValues.contribution_adjustment < 0 &&
      formValues.broker_fee > 0
    )
      return {
        label: t('contracts.form.broker_fee_reduced')
      };
    if (type === 'prevoyance' || type === 'rcphx') {
      return {
        label: t('contracts.form.broker_fee_prevoyance')
      };
    }
    if (supplier === 'ml') {
      return {
        label: t('contracts.form.broker_fee_ml')
      };
    }
    return {
      label: t('contracts.form.broker_fee', {
        min: 0,
        max: getMaxBrokerFee()
      })
    };
  };

  const getBrokerFeeTooltip = () => {
    if (formValues && formValues.contribution_adjustment < 0) return true;
    if (type === 'prevoyance') return { name: 'broker_fee_info_prevoyance' };
    return {
      name: 'broker_fee_info',
      params: { max: (getMaxBrokerFee() || 20) * 12 }
    };
  };

  const maxComissionFee = useMemo(() => {
    const key = supplier ? `${type}${supplier}` : type;
    return maxComissionFeeMap[key] || DEFAULT_COMMISSION_FEE;
  }, [type, supplier]);

  const getCommissionFeeConfig = () => {
    if (type === 'rcgls') {
      return {
        label: t('contracts.form.commission_fee_rcgls', {
          min: 0,
          max: maxComissionFee
        }),
        tooltipLabel: t('contracts.form.tooltip.commission_fee_rcgls')
      };
    }

    if (
      formValues?.contribution_adjustment < 0 &&
      formValues?.commission_fee > 10
    ) {
      return {
        label: t('contracts.form.commission_fee_reduced'),
        tooltipLabel: t('contracts.form.tooltip.commission_fee')
      };
    }

    return {
      label: t('contracts.form.commission_fee', {
        min: 0,
        max: maxComissionFee
      }),
      tooltipLabel: t('contracts.form.tooltip.commission_fee_info', {
        max: maxComissionFee
      })
    };
  };

  return [
    {
      name: ['payment_type'],
      label: 'payment_type',
      rules: [{ required: true }],
      input: (
        <Radio.Group
          buttonStyle="solid"
          onChange={(change) =>
            setFormValues({
              ...formValues,
              payment_type: change.target.value
            })
          }
        >
          {formValues &&
            (type !== 'mriml' || formValues.fraction_type !== 'YEARLY') && (
              <Radio.Button
                value="DIRECTDEBIT"
                disabled={
                  (terminationNonPayment && type === 'association') ||
                  formValues?.subscriber_type === 'LE_BLDG_SELLER_OF_GOODS'
                }
              >
                {t('contracts.form.options.DIRECTDEBIT')}
              </Radio.Button>
            )}
          ,
          {formValues && formValues.fraction_type !== 'MONTHLY' && (
            <Radio.Button value="CARD">
              {t('contracts.form.options.CARD')}
            </Radio.Button>
          )}
        </Radio.Group>
      ),
      tooltip: true
    },
    ...(type === 'mri' ||
    type === 'mriml' ||
    type === 'pnoml' ||
    type === 'association'
      ? []
      : [
          {
            name: ['company_in_creation'],
            label: 'company_in_creation',
            tooltip: true,
            input: <PreRequisitesSwitch />,
            valuePropName: 'checked'
          }
        ]),
    ...(!displayRestricted
      ? [
          ...(isAmendment === -1
            ? [
                {
                  name: ['paperwork_fee'],
                  label: t(
                    `${
                      type === 'prevoyance'
                        ? 'paperwork_fee_prev'
                        : type === 'pnoml'
                        ? 'paperwork_fee_pnoml'
                        : type === 'rcgls'
                        ? 'paperwork_fee_rcgls'
                        : 'paperwork_fee'
                    }`
                  ),
                  rules: [{ required: true }],
                  tooltip: true,
                  tooltipLabel:
                    type === 'pnoml'
                      ? t('contracts.form.tooltip.paperwork_fee_pnoml')
                      : type === 'rcgls'
                      ? t('contracts.form.tooltip.paperwork_fee_rcgls')
                      : type === 'prevoyance'
                      ? t('contracts.form.tooltip.paperwork_fee_prev')
                      : t('contracts.form.tooltip.paperwork_fee'),
                  input: (
                    <InputNumber
                      min={
                        params?.controlled_fit?.selected
                          ? params?.controlled_fit?.administration_fees_minVal
                          : 0
                      }
                      max={getMaxPaperworkFee()}
                      disabled={params?.blocked_fit?.selected}
                    />
                  )
                }
              ]
            : []),
          ...(formValues && formValues.paperwork_fee && isAmendment === -1
            ? [
                {
                  name: ['paperwork_fee_payment'],
                  label: 'paperwork_fee_payment',
                  rules: [{ required: true }],
                  layout: {
                    wrapperCol: {
                      xs: { span: 24 }
                    }
                  },
                  input: (
                    <Radio.Group style={{ flexFlow: 'column' }}>
                      <Radio style={radioStyle} value="FREE">
                        {t(
                          `contracts.form.options.${
                            type === 'prevoyance' ? 'FREE_PREV' : 'FREE'
                          }`
                        )}
                      </Radio>
                      <Radio style={radioStyle} value="CB">
                        {t(
                          `contracts.form.options.${
                            type === 'prevoyance' ? 'CB_PREV' : 'CB'
                          }`
                        )}
                      </Radio>
                      {formValues.paperwork_fee < 101 &&
                        user &&
                        !user?.insurer?.paperwork_fee_restriction &&
                        formValues.payment_type !== 'CARD' && (
                          <Radio
                            style={radioStyle}
                            value="ASSURMAX"
                            disabled={
                              formValues &&
                              formValues.paperwork_fee > 10 &&
                              type === 'prevoyance'
                            }
                          >
                            {t(
                              `contracts.form.options.${
                                type === 'prevoyance'
                                  ? 'ASSURMAX_PREV'
                                  : 'ASSURMAX'
                              }`
                            )}
                          </Radio>
                        )}
                    </Radio.Group>
                  )
                }
              ]
            : [])
        ]
      : []),
    ...(supplier !== 'ml'
      ? [
          {
            name: ['sworn_statement'],
            label: 'sworn_statement',
            input: (
              <Switch
                checkedChildren={t('quotes.form.switch.yes')}
                unCheckedChildren={t('quotes.form.switch.no')}
                defaultChecked
              />
            ),
            valuePropName: 'checked',
            defaultChecked: true,
            tooltip: true,
            step: 1
          }
        ]
      : [])
  ].filter(({ name }) => {
    if (Array.isArray(name)) {
      return !name.some((n) => hiddenFieldNameMap.has(n));
    }
    return hiddenFieldNameMap.has(name);
  });
};

export default useSharedFields;
